import AlertBanner from 'ext-ui/components/AlertBanner/AlertBanner'
import Button from 'ext-ui/components/Button/Button'
import SingleDropdown, {
  DropdownValue,
} from 'ext-ui/components/Dropdown/SingleDropdown/SingleDropdown'
import TextInput, { TextInputRef } from 'ext-ui/components/TextInput/TextInput'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import React, { useEffect, useRef, useState } from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import * as style from 'src/app/CardFeedCreate/CardFeedCreate.style'
import mastercardLogoUrl from 'src/assets/images/issuerLogos/mastercard.svg'
import visaLogoUrl from 'src/assets/images/issuerLogos/visa.svg'
import SvgChevronLeft from 'src/components/Svgs/SvgChevronLeft'
import { useTranslate } from 'src/localization/Trns'

import {
  type CardNetwork,
  type FeedFields,
  type FieldKey,
  getFieldError,
  isFeedDetailsValid,
  NETWORK_FIELDS,
  NETWORK_VALUES,
} from './CardFeedCreate'

interface StepEnterFeedDetailsProps {
  network: CardNetwork | undefined
  fields: FeedFields
  onNetworkChange: (network: CardNetwork | undefined) => void
  onFieldsChange: (fields: FeedFields) => void
  onBack: () => void
  onNext: () => void
}

const isCardNetwork = (value: string): value is CardNetwork =>
  (NETWORK_VALUES as readonly string[]).includes(value)

const StepEnterFeedDetails: React.FC<StepEnterFeedDetailsProps> = ({
  network,
  fields,
  onNetworkChange,
  onFieldsChange,
  onBack,
  onNext,
}) => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const firstFieldRef = useRef<TextInputRef | null>(null)
  // Surface format errors only after a field is blurred, so we don't flag a
  // Delivery ID as malformed while the user is still mid-type.
  const [touched, setTouched] = useState<Partial<Record<FieldKey, boolean>>>({})

  // Switching networks swaps the field set (and the parent clears values), so
  // any "touched" marks from the previous network no longer apply.
  useEffect(() => setTouched({}), [network])

  const mastercardIcon = (
    <img src={mastercardLogoUrl} alt="" width={32} height={20} />
  )
  const visaIcon = <img src={visaLogoUrl} alt="" width={32} height={10} />

  const networkOptions = [
    {
      value: 'mastercard',
      label: translate('Mastercard'),
      icon: mastercardIcon,
    },
    { value: 'visa', label: translate('Visa'), icon: visaIcon },
  ]

  const selectedValue: DropdownValue | undefined = network
    ? {
        value: network,
        label:
          network === 'mastercard'
            ? translate('Mastercard')
            : translate('Visa'),
      }
    : undefined

  const selectedNetworkIcon =
    network === 'mastercard'
      ? mastercardIcon
      : network === 'visa'
        ? visaIcon
        : undefined

  const handleNetworkSelect = (value: DropdownValue): void => {
    if (value.value === '') {
      onNetworkChange(undefined)
      return
    }
    if (isCardNetwork(value.value)) {
      onNetworkChange(value.value)
      analytics.track('card feeds create', {
        action: 'network selected',
        network: value.value,
        step: 2,
      })
      setTimeout(() => firstFieldRef.current?.focus(), 0)
    }
  }

  const handleBackClick = (): void => {
    analytics.track('card feeds create', { action: 'back clicked', step: 2 })
    onBack()
  }

  const handleNextClick = (): void => {
    analytics.track('card feeds create', { action: 'next clicked', step: 2 })
    onNext()
  }

  const canProceed = isFeedDetailsValid(network, fields)

  return (
    <div>
      <header css={style.stepHeader}>
        <span css={style.stepEyebrow}>{translate('Step 2 of 3')}</span>
        <p css={style.stepHeading}>{translate('Enter your feed details')}</p>
        <p css={style.stepBody}>
          {translate(
            'Once your bank has enabled the feed, enter the details provided by them. Connect one network at a time - you can add more feeds later.',
          )}
        </p>
      </header>

      <div css={style.fieldGroup}>
        <SingleDropdown
          label={translate('Card Network')}
          placeholder={translate('Select Card Network')}
          selectedValue={selectedValue}
          onSelect={handleNetworkSelect}
          options={networkOptions}
          iconLeft={selectedNetworkIcon}
          noneLabel=""
          data-testid="card-feed-create-network"
        />
      </div>

      {network !== undefined && (
        <>
          {NETWORK_FIELDS[network].map((def, idx) => {
            const error = getFieldError(def, fields[def.key])
            return (
              <div key={def.key} css={style.fieldGroup}>
                <TextInput
                  ref={idx === 0 ? firstFieldRef : undefined}
                  label={translate(def.labelKey)}
                  tooltip={
                    def.tooltipKey ? (
                      <TooltipIcon>{translate(def.tooltipKey)}</TooltipIcon>
                    ) : undefined
                  }
                  placeholder={translate(def.placeholderKey)}
                  value={fields[def.key] ?? ''}
                  onChange={(e): void =>
                    onFieldsChange({ ...fields, [def.key]: e.target.value })
                  }
                  onBlur={(): void =>
                    setTouched((prev) => ({ ...prev, [def.key]: true }))
                  }
                  onFocus={(): void =>
                    analytics.track('card feeds create', {
                      action: 'reference id focused',
                      step: 2,
                    })
                  }
                  errorText={
                    touched[def.key] && error ? translate(error) : undefined
                  }
                  testId={def.testId}
                />
              </div>
            )
          })}

          <AlertBanner
            variant="soft"
            title={translate('What happens next:')}
            data-testid="card-feed-create-what-happens-next"
          >
            <ul css={style.whatHappensList}>
              <li>{translate('Review and accept the enrollment terms')}</li>
              <li>
                {translate(
                  'Feed connects & transactions sync in real time (brief wait may apply)',
                )}
              </li>
              <li>
                {translate('Assign cards to cardholders, or invite new ones')}
              </li>
            </ul>
          </AlertBanner>
        </>
      )}

      <div css={style.stepActions}>
        <button
          type="button"
          css={style.backButton}
          onClick={handleBackClick}
          data-testid="card-feed-create-back-2"
        >
          <SvgChevronLeft
            width={16}
            height={16}
            viewBox="0 0 24 24"
            aria-hidden="true"
          />
          {translate('Go Back')}
        </button>
        <Button
          variant="primary"
          onClick={handleNextClick}
          disabled={!canProceed}
          data-testid="card-feed-create-next-2"
        >
          {translate('Next: Review Enrollment Terms')}
        </Button>
      </div>
    </div>
  )
}

export default StepEnterFeedDetails
